import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {buildLeagueSlug} from "../../../utility/slug-helper";
import {ActivatedRoute, Router} from "@angular/router";
import {environment} from "@environment";
import {Meta, Title} from "@angular/platform-browser";
import {
  CalendarViewType,
  Country,
  DEFAULT_PAGE_SIZE,
  EventItem,
  League,
  LeagueFilter,
  LeagueStandingsFilter,
  LeagueStatus,
  Lookups,
  Message,
  PagedFilter,
  Pages,
  PublicSearchFilter,
  Sport
} from "@model";
import {LeagueService, LocalStorageService} from "@services";
import {Observable} from 'rxjs';
import {TranslocoService} from "@ngneat/transloco";
import {PagingComponent} from "../../../components/paging.component";
import {FilterGroup, FilterGroupType, FilterItem} from "@components";

@Component({
    selector: 'app-league-calendar',
    templateUrl: './league-calendar.component.html',
    styleUrls: ['./league-calendar.component.scss'],
    standalone: false
})
export class LeagueCalendarComponent extends PagingComponent<League> implements OnInit, OnDestroy{
  @Input() leaguesCountries: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input() leaguesSports: Sport[] = [];
  @Input() leaguesStatuses: Lookups[] = [];
  @Input() listOfLeagues: League[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages: Message[] | undefined;
  filterGroups: FilterGroup[] = [];
  searchFilter: PublicSearchFilter = {};

  environment: any = environment;
  activeStatus?: number;

  constructor(private router: Router,
              private leagueService: LeagueService,
              private route: ActivatedRoute,
              private localStorageService: LocalStorageService,
              private translocoService: TranslocoService,
              private metaService: Meta,
              private titleService: Title) {
    super();
  }


  override ngOnInit(): void {
    super.ngOnInit();
    this.setSEOData();
    this.handleExploreResult(this.listOfLeagues);
    this.buildFilteringParams()
  }

  setSEOData(): void {
    const origin = this.document.location.origin;
    const url = `${origin}${this.document.location.pathname}`;
    const description = 'Find the race that inspires you to move';
    const heroImage = `${origin}/assets/img/hero2-mobile.webp`;

    this.titleService.setTitle(`Leagues | ${environment.site_name}`);
    this.metaService.updateTag({name: 'description', content: description});

    this.metaService.updateTag({property: 'og:url', content: url});
    this.metaService.updateTag({property: 'og:title', content: environment.site_name});
    this.metaService.updateTag({property: 'og:type', content: 'website'});
    this.metaService.updateTag({property: 'og:site_name', content: environment.site_name});
    this.metaService.updateTag({property: 'og:description', content: description});
    this.metaService.updateTag({property: 'og:image', content: heroImage});

    this.metaService.updateTag({name: 'twitter:card', content: 'summary_large_image'});
    this.metaService.updateTag({name: 'twitter:title', content: environment.site_name});
    this.metaService.updateTag({name: 'twitter:description', content: description});
    this.metaService.updateTag({name: 'twitter:image', content: heroImage});

    this.setCanonicalUrl(url);
  }

  private setCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = this.document.createElement('link') as HTMLLinkElement;
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  protected fetchPage(page: number): Observable<League[]> {
    return this.leagueService.findPublicLeagues(this.createFilter())
  }

  protected override afterSetItems(items: League[]): void {
    this.listOfLeagues = items;
  }

  createFilter(): PagedFilter<LeagueFilter> {
    const status = this.searchFilter.status;
    const hasStatus = status !== null && status !== undefined;

    const statuses = hasStatus
        ? (status === LeagueStatus.ACTIVE
            ? [LeagueStatus.INACTIVE, LeagueStatus.ACTIVE]
            : [status])
        : undefined;

    return new PagedFilter(new Pages(DEFAULT_PAGE_SIZE, this.page), {
      name: this.searchFilter.name !== "" ? this.searchFilter.name : undefined,
      startDate: undefined,
      endDate: undefined,
      countries: undefined,
      sports: undefined,
      leagueStatuses: statuses,
    });
  }


  private buildFilteringParams(filter?: LeagueStandingsFilter) {
    this.filterGroups = [
      new FilterGroup('status', 'status', '',
          FilterGroupType.SINGLE_SELECT_BUTTONS,
          false,
          [
            new FilterItem(undefined,undefined, 'app.common.all', true),
            new FilterItem(LeagueStatus.ACTIVE,undefined, 'league.status.1', false),
            new FilterItem(LeagueStatus.FINISHED, undefined, 'league.status.2', false),
          ]),
    ];
  }

  goToSeriesDetailsPage(item: League): void {
    const slug = buildLeagueSlug(item.id!, item);
    this.router.navigate([`/leagues/details/${slug}/overview`]);
  }

  initFilter(filter: PublicSearchFilter) {
    this.searchFilter = filter;
    this.activeStatus = filter.status;
  }

  filterChanged(filter: PublicSearchFilter) {
    this.searchFilter = filter;
    this.activeStatus = filter.status;
    this.onExplore(false);
  }


  onFilterChange(result: Record<string, any>): void {
    this.listOfLeagues = [];
    this.searchFilter = new PublicSearchFilter();
    this.searchFilter.status = result['status']
    this.searchFilter.name = result['name']
    this.activeStatus = this.searchFilter.status;
    this.onExplore(false);
  }


  protected readonly League = League;
  protected readonly CalendarViewType = CalendarViewType;
}

